import { OrderFailReason, OrderStatus, OrderType } from "./order";

/** Шаг ряда аналитики заказов, как StatsGranularity на бэке */
export enum StatsGranularity {
    Hour = 0,
    Day = 1,
    Month = 2,
    Year = 3,
    All = 4,
}

/** Суммы — копейки; json-bigint отдаёт большие значения как bigint */
export type Money = number | bigint;

export type OrderStatusTotals = {
    status: OrderStatus;
    count: number;
    sum: Money;
    percent: number;
};

/**
 * Строка сводки: итог «Все игры» (gameId = null) или одна игра.
 * Проценты групп — от всех предметов строки; successRate — успешные / (успешные + неуспешные).
 */
export type OrderSummaryRow = {
    gameId?: number | null;
    gameName: string;
    totalCount: number;
    totalSum: Money;
    successCount: number;
    successSum: Money;
    successPercent: number;
    failedCount: number;
    failedSum: Money;
    failedPercent: number;
    refundedCount: number;
    refundedSum: Money;
    inProgressCount: number;
    inProgressSum: Money;
    inProgressPercent: number;
    attentionCount: number;
    attentionSum: Money;
    attentionPercent: number;
    successRate: number;
    byStatus: OrderStatusTotals[];
};

/** Точка ряда; пустые интервалы бэк не возвращает */
export type OrderSummaryPoint = {
    bucketUtc: string;
    totalCount: number;
    successCount: number;
    failedCount: number;
    inProgressCount: number;
    attentionCount: number;
    successRate: number;
};

/** Виновник неуспеха; TradeHold и ExtensionIssues — по стороне последнего офера заказа */
export enum OrderFailCulprit {
    Seller = 0,
    Buyer = 1,
    Platform = 2,
    Supplier = 3,
    Admin = 4,
    Other = 5,
    P2PUndetermined = 6,
}

export const OrderFailCulpritDescription: Record<OrderFailCulprit, string> = {
    [OrderFailCulprit.Seller]: "Продавец",
    [OrderFailCulprit.Buyer]: "Покупатель",
    [OrderFailCulprit.Platform]: "Платформа (боты)",
    [OrderFailCulprit.Supplier]: "Поставщик",
    [OrderFailCulprit.Admin]: "Администратор",
    [OrderFailCulprit.Other]: "Прочее",
    [OrderFailCulprit.P2PUndetermined]: "P2P: сторона не определена",
};

export type FailCulpritCount = { culprit: OrderFailCulprit; count: number };

/** failReason = null — неуспешные без причины */
export type FailReasonCount = { failReason?: OrderFailReason | null; count: number };

export type FailReasonRow = {
    failReason?: OrderFailReason | null;
    count: number;
    sum: Money;
    percentOfFailed: number;
    /** Накопительный % в порядке бэка (по убыванию количества) — для Парето */
    cumulativePercent: number;
    culprits: FailCulpritCount[];
};

export type FailCulpritRow = {
    culprit: OrderFailCulprit;
    count: number;
    sum: Money;
    percent: number;
    reasons: FailReasonCount[];
};

export type FailReasonGameCell = {
    failReason?: OrderFailReason | null;
    gameId: number;
    gameName: string;
    count: number;
};

export type FailReasonPoint = {
    bucketUtc: string;
    totalFailed: number;
    /** Все причины из topReasons в том же порядке */
    reasons: FailReasonCount[];
    otherCount: number;
};

/** Неуспешные — Failed и Refunded; штуки — предметы заказов */
export type OrderFailReasonsReport = {
    totalFailed: number;
    totalFailedSum: Money;
    reasons: FailReasonRow[];
    byCulprit: FailCulpritRow[];
    reasonByGame: FailReasonGameCell[];
    topReasons: Array<OrderFailReason | null>;
    series: FailReasonPoint[];
};

/** Группа неуспешных с одинаковой сигнатурой комментария: текст ошибки без номеров, ников, дат (#N, USER, DATE…) */
export type FailDetailGroup = {
    failReason?: OrderFailReason | null;
    culprit: OrderFailCulprit;
    steamCode?: number | null;
    /** Имя кода из EResult; null — код Steam неизвестен */
    steamCodeName?: string | null;
    signature: string;
    /** Позиции заказов */
    count: number;
    ordersCount: number;
    sum: Money;
    percentOfFailed: number;
    percentOfReason: number;
    /** Полный комментарий последнего заказа группы */
    exampleComment?: string | null;
    lastOrderId: number;
};

/** Группы за пределами топ-200 */
export type FailDetailOther = {
    groupsCount: number;
    count: number;
    sum: Money;
    percentOfFailed: number;
};

export type SteamErrorCodeRow = {
    code: number;
    name?: string | null;
    count: number;
    sum: Money;
    percentOfFailed: number;
    /** До 3 причин, в которых код встречается чаще всего */
    reasons: FailReasonCount[];
};

export type OrderFailDetailsReport = {
    totalFailed: number;
    totalFailedSum: Money;
    groups: FailDetailGroup[];
    other: FailDetailOther;
    steamCodes: SteamErrorCodeRow[];
};

/** Этап исполнения заказа для аналитики скорости, как OrderTimingStage на бэке */
export enum OrderTimingStage {
    CreatedToRequested = 0,
    RequestedToReceived = 1,
    ReceivedToSentToBuyer = 2,
    SentToBuyerToCompleted = 3,
    Total = 4,
}

/** Длительности — секунды (до часа — точно до секунды, дальше — до минуты); null — заказов на этапе нет */
export type OrderTimingStats = {
    stage: OrderTimingStage;
    count: number;
    medianSeconds?: number | null;
    p90Seconds?: number | null;
    avgSeconds?: number | null;
};

export type OrderTimingGameRow = { gameId: number; gameName: string; stages: OrderTimingStats[] };

export type OrderTimingTypeRow = { type: OrderType; stages: OrderTimingStats[] };

/** isManualStart — ManualMode без причины: новый заказ продавца с ручным запуском */
export type StuckStatusCount = { status: OrderStatus; isManualStart: boolean; count: number };

export type StuckOrder = {
    orderId: number;
    gameId: number;
    gameName: string;
    status: OrderStatus;
    /** ManualMode без причины — новый заказ продавца с ручным запуском */
    isManualStart: boolean;
    type: OrderType;
    createdAt: string;
    ageHours: number;
};

/** Нефинализированные заказы периода, созданные раньше, чем stuckHours назад */
export type StuckOrders = {
    stuckHours: number;
    total: number;
    byStatus: StuckStatusCount[];
    /** До 50 самых старых */
    oldest: StuckOrder[];
};

export type OrderTimingReport = {
    /** Все этапы по порядку, в том числе пустые */
    stages: OrderTimingStats[];
    byGame: OrderTimingGameRow[];
    byType: OrderTimingTypeRow[];
    stuck: StuckOrders;
};

export type OrderSummaryReport = {
    total: OrderSummaryRow;
    byGame: OrderSummaryRow[];
    series: OrderSummaryPoint[];
};
